/**
 * useStations — replaces AngularJS StationService.
 * Fetches from API or falls back to localStorage.
 */

import { useCallback, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '@/env';
import type { Station } from '@/types';

interface UseStationsReturn {
  stations: Station[];
  loading: boolean;
  fetchStations: (loadRemote?: boolean) => Promise<Station[]>;
}

export function useStations(): UseStationsReturn {
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchStations = useCallback(async (loadRemote = false): Promise<Station[]> => {
    const cached = window.localStorage.getItem('stations');

    if (!loadRemote && cached) {
      const local = JSON.parse(cached) as Station[];
      setStations(local);
      return local;
    }

    setLoading(true);
    try {
      const { data } = await axios.get<{ success: boolean; data: Station[] }>(
        `${BASE_URL}stations`,
      );
      if (data.success) {
        window.localStorage.setItem('stations', JSON.stringify(data.data));
        setStations(data.data);
        return data.data;
      }
      throw new Error('Failed to fetch stations');
    } catch {
      if (cached) {
        const local = JSON.parse(cached) as Station[];
        setStations(local);
        return local;
      }
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  return { stations, loading, fetchStations };
}
